import { useMutation } from "@tanstack/react-query";
import { api, queryClient } from "../context";
import { HistoricRequestCardDetails, Request } from "../interfaces";
import { RequestAPIResponse } from "./requests/useRequest";

interface UseDeletePrescriptionDetailProps {
  requestId: string;
  request?: Request;
  onSuccess?: () => void;
}

interface DeleteDetailContext {
  previousRequest?: Request;
}

export const useDeletePrescriptionDetail = ({
  requestId,
  request,
  onSuccess,
}: UseDeletePrescriptionDetailProps) => {
  return useMutation({
    mutationFn: (detail: HistoricRequestCardDetails) => {
      if (!detail.id) throw new Error("missing detail id");
      return api({
        url: `/requests/${requestId}/detail/${detail.id}`,
        method: "DELETE",
      });
    },
    onMutate: async (detail): Promise<DeleteDetailContext> => {
      if (!request) return { previousRequest: request };
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ["request", requestId] });
      // Get the current request data
      const requestData = queryClient.getQueryData<RequestAPIResponse>([
        "request",
        requestId,
      ]);
      // Remove the detail from the request
      const updatedRequest = {
        ...requestData,
        details: requestData?.details?.filter((d) => d.id !== detail.id),
      };
      // Optimistically update to the new value
      queryClient.setQueryData(["request", requestId], updatedRequest);
      return { previousRequest: request };
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error, _, context) => {
      console.error("Error deleting request detail", error);
      if (context?.previousRequest) {
        queryClient.setQueryData(
          ["request", requestId],
          context.previousRequest,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["request", requestId] });
    },
  });
};
